/**
 * Zernio's structured publish-failure taxonomy (post targets and post.*
 * webhooks carry `errorCategory` + `errorSource`). Shared by the API (persist),
 * notifications (email / Telegram) and the web UI (display), so the wording of
 * "what went wrong and who has to act" is the same everywhere.
 */

export const PUBLISH_ERROR_CATEGORIES = [
  "auth_expired",
  "user_content",
  "user_abuse",
  "platform_rate_limit",
  "quota_exhausted",
  "account_issue",
  "platform_rejected",
  "platform_error",
  "system_error",
  "unknown",
] as const;
export type PublishErrorCategory = (typeof PUBLISH_ERROR_CATEGORIES)[number];

export const PUBLISH_ERROR_SOURCES = ["user", "platform", "system"] as const;
export type PublishErrorSource = (typeof PUBLISH_ERROR_SOURCES)[number];

export interface PublishErrorCategoryInfo {
  label: string;
  /** What to do about it, phrased for the person reading the notification. */
  action: string;
}

export const PUBLISH_ERROR_CATEGORY_INFO: Record<PublishErrorCategory, PublishErrorCategoryInfo> = {
  auth_expired: { label: "Account disconnected", action: "Reconnect the social account, then reschedule." },
  user_content: { label: "Content not accepted", action: "Fix the post (format, length or media specs), then reschedule." },
  user_abuse: { label: "Rate limit / spam check", action: "Post less often from this account, then retry later." },
  platform_rate_limit: { label: "Platform throttling", action: "The platform is throttling requests; retry later." },
  quota_exhausted: { label: "Daily API quota used up", action: "Publishing resumes when the platform's quota resets." },
  account_issue: { label: "Account setup problem", action: "Fix the account setup on the platform (e.g. account type)." },
  platform_rejected: { label: "Rejected by platform policy", action: "Change the content; the platform refused it." },
  platform_error: { label: "Platform outage", action: "Temporary platform error; retry later." },
  system_error: { label: "Zernio error", action: "Zernio-side problem; retry, or contact Zernio support." },
  unknown: { label: "Unknown error", action: "Check the error details." },
};

export const PUBLISH_ERROR_SOURCE_LABEL: Record<PublishErrorSource, string> = {
  user: "you need to act",
  platform: "the platform must recover",
  system: "Zernio must fix it",
};

export function isPublishErrorCategory(value: unknown): value is PublishErrorCategory {
  return typeof value === "string" && (PUBLISH_ERROR_CATEGORIES as readonly string[]).includes(value);
}

export function isPublishErrorSource(value: unknown): value is PublishErrorSource {
  return typeof value === "string" && (PUBLISH_ERROR_SOURCES as readonly string[]).includes(value);
}

export interface PublishFailureFields {
  errorMessage?: string | null;
  errorCategory?: string | null;
  errorSource?: string | null;
  platformError?: string | null;
}

/**
 * One-line summary of a structured failure, e.g.
 * "Account disconnected (you need to act): Reconnect the social account, then reschedule."
 * Returns null when there is no category or source to describe.
 */
export function describePublishFailure(f: PublishFailureFields): string | null {
  const category = isPublishErrorCategory(f.errorCategory) ? f.errorCategory : null;
  const source = isPublishErrorSource(f.errorSource) ? f.errorSource : null;
  if (!category && !source) {
    // An unrecognised category (Zernio added one) still beats saying nothing.
    return f.errorCategory ? `Category: ${f.errorCategory}` : null;
  }
  const info = category ? PUBLISH_ERROR_CATEGORY_INFO[category] : null;
  const head = info?.label ?? "Publish failed";
  const who = source ? ` (${PUBLISH_ERROR_SOURCE_LABEL[source]})` : "";
  return info ? `${head}${who}: ${info.action}` : `${head}${who}`;
}
